import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import { ProcessResponseDTOStatus, ProcessUpdateDtoAssessmentType } from '@/api/generated';
import ChipSelect from '@/components/fields/ChipSelect';
import Field from '@/components/fields/Field';
import GeneralStatusDropdownSelect from '@/components/fields/GeneralStatusDropdownSelect';
import TextInput from '@/components/fields/TextInput';
import typography from '@/theme/typography';

interface BasicInfoTabProps {
  availableStatuses?: ProcessResponseDTOStatus[];
  isCreateProcess?: boolean;
}

const BasicInfoTab: FC<BasicInfoTabProps> = ({ availableStatuses, isCreateProcess = false }) => {
  const { t } = useTranslation(['processes', 'settings']);
  const { spacing } = useTheme();

  return (
    <Stack
      width="100%"
      sx={{
        '& .MuiFormLabel-root': { textAlign: 'start' },
        '& .MuiFormControl-root': { pb: 0 },
        '& .DropdownSelect-expandMoreIcon': { color: 'text.primary' }
      }}
    >
      {!isCreateProcess && (
        <Stack direction="row" justifyContent="space-between" width="100%" p={3}>
          <Typography variant="body1" sx={{ fontWeight: typography.fontWeightMedium }}>
            {t('settings:processSettings.form.basicInfo.title')}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body3" color="grey.700" fontWeight={typography.fontWeightMedium}>
              {t('layouts.processes.columns.status')}
            </Typography>
            <Box>
              <Field
                component={GeneralStatusDropdownSelect}
                name="status"
                label={t('status')}
                innerProps={{
                  activeOptions: availableStatuses || [],
                  sx: {
                    '& .DropdownSelect-control': {
                      minWidth: spacing(18),
                      minHeight: spacing(4)
                    }
                  }
                }}
              />
            </Box>
          </Stack>
        </Stack>
      )}
      <Stack
        sx={{
          maxWidth: '60%',
          minWidth: 300,
          mx: 3,
          rowGap: 2,
          '& .MuiFormControl-root': { p: 0, width: '100%' }
        }}
      >
        {!isCreateProcess && (
          <Field
            component={TextInput}
            name="name"
            label={t('layouts.processes.columns.processName')}
            withFormControl
            innerProps={{ markAsRequired: true }}
          />
        )}
        {/* <Field
          component={CheckboxToggle}
          name="processType"
          label={t('settings:processSettings.form.basicInfo.sharedProcess')}
          innerProps={{
            sx: {
              py: 0
            }
          }}
        /> */}
        <Field
          component={ChipSelect}
          name="assessmentType"
          withFormControl
          label={t('settings:processSettings.form.basicInfo.assessmentType')}
          outerSx={{ p: 0, gridRowStart: 4 }}
          innerProps={{
            color: 'primary',
            options: [
              {
                label: t('settings:processSettings.form.options.assessmentType.external'),
                value: ProcessUpdateDtoAssessmentType.EXTERNAL
              },
              {
                label: t('settings:processSettings.form.options.assessmentType.internal'),
                value: ProcessUpdateDtoAssessmentType.INTERNAL
              },
              {
                label: t('settings:processSettings.form.options.assessmentType.any'),
                value: ProcessUpdateDtoAssessmentType.ANY
              }
            ],
            sx: { alignSelf: 'flex-start' },
            enforcedValue: true,
            markAsRequired: true
          }}
        />
        <Field
          component={TextInput}
          name="description"
          label={t('settings:processSettings.form.basicInfo.description')}
          withFormControl
          innerProps={{
            placeholder: t('settings:processSettings.form.basicInfo.descriptionPlaceholder'),
            multiline: true,
            InputProps: {
              sx: {
                px: 1,
                '&.MuiInputBase-root textarea.MuiInputBase-input': {
                  minHeight: 100,
                  px: 0
                }
              }
            }
          }}
        />
      </Stack>
    </Stack>
  );
};

export default BasicInfoTab;
